import React from 'react'
import Helmet from 'react-helmet'

import { PageRenderer, useCmsBlock } from '@library/cms-blocks'
import type { CmsFetcher, OnTriggerPromoCode } from '@library/cms-blocks'

import { getCmsSeo } from './seo'

const CMS_PAGE_CONTENT_TYPE = 'cms_page'

type Props = {
  pageUrl: string
  cmsFetcher: CmsFetcher
  onTriggerPromocode: OnTriggerPromoCode
  renderNotFound?: () => JSX.Element | null
}

// Resolves and renders any CMS page from its `pageUrl` — no per-page code.
export function DynamicCmsPage({ pageUrl, cmsFetcher, onTriggerPromocode, renderNotFound }: Props) {
  const { data, loading, error } = useCmsBlock(
    { contentType: CMS_PAGE_CONTENT_TYPE, pageUrl },
    cmsFetcher,
  )

  // Only on client-side nav; SSR pre-populates the store before render.
  if (loading) {
    return null
  }

  // No entry or failed fetch — SSR already 404'd in fetchData before here.
  if (error || !data) {
    return renderNotFound ? renderNotFound() : null
  }

  const seo = getCmsSeo(data, pageUrl)

  return (
    <div data-testid="dynamic-cms-page">
      <Helmet title={seo.title} link={[{ rel: 'canonical', href: seo.canonical }]}>
        <meta name="description" content={seo.description} />
        <meta name="robots" content={seo.robots} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:image" content={seo.ogImage} />
      </Helmet>
      <PageRenderer page={data} onTriggerPromocode={onTriggerPromocode} />
    </div>
  )
}
